"use client";

/**
 * EmailSoundForm — the "Email me this sound" bridge for stranded mobile users.
 *
 * Mobile is ~half of /sounds visitors but converts at a third of desktop's
 * rate because the USB install needs a computer. This form emails the user a
 * download link + the 3 install steps so the session isn't lost.
 *
 * Mounted on two surfaces (pass `surface` so funnel data can tell them apart):
 *   - 'sound_page' — pre-download, under the play controls (the mobile fix)
 *   - 'post_download_receipt' — inside the receipt card
 * (The Chime Studio PostDownloadPanel has its own older inline copy of this
 * flow; consolidation is tracked separately.)
 *
 * Abuse posture (mirrors /api/email-sound): request-proof headers, a hidden
 * honeypot field, and server-side per-IP / per-recipient / global caps. The
 * 429 message always points at the direct download as the fallback.
 *
 * Styling uses the --tls-* token layer only — both themes are first-class.
 */

import { useCallback, useId, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/lib/utils";
import { withRequestProofHeaders } from "@/lib/request-proof";
import { trackEmailFileRequested } from "@/lib/tracking";
import { useSessionEmail } from "@/hooks/useSessionEmail";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** §05.4: write the fix, not the complaint. */
const EMAIL_FORMAT_MESSAGE = "Enter an address in the form name@example.com";

export interface EmailSoundFormProps {
  soundId: string;
  /** Funnel attribution for where this form lives. */
  surface: "sound_page" | "post_download_receipt";
  /** Disclosure-row label. Default speaks to the pre-download mobile case. */
  label?: string;
  /** One-line explainer shown when expanded. */
  helper?: string;
  /**
   * Visual weight of the collapsed trigger.
   * - "subtle" (default): quiet muted text row — desktop + post-download receipt.
   * - "primary": on mobile, a ruled co-primary disclosure with a reassurance
   *   subtitle so stranded phone users see a path that doesn't dead-end at the
   *   USB step; reverts to the subtle row at `sm:` and up (desktop already
   *   converts, so it is left untouched). CRO: mobile email-bridge emphasis.
   */
  emphasis?: "subtle" | "primary";
  defaultOpen?: boolean;
  className?: string;
}

type SendState = "idle" | "loading" | "done" | "error";

export function EmailSoundForm({
  soundId,
  surface,
  label = "Email me this sound",
  helper = "No USB drive handy? We'll send the file and the 3 install steps: open it on your computer later.",
  emphasis = "subtle",
  defaultOpen = false,
  className,
}: EmailSoundFormProps) {
  const isPrimary = emphasis === "primary";
  const [open, setOpen] = useState(defaultOpen);
  // §11.4/§11.6 WCAG 3.3.7 Redundant Entry: pre-fill from whatever address
  // was entered earlier this session (any of the three email-collecting
  // forms), with a visible "change it" escape hatch.
  const { email, setEmail, isPrefilled, changeEmail, rememberEmail } = useSessionEmail();
  const [joinNewsletter, setJoinNewsletter] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [state, setState] = useState<SendState>("idle");
  const [message, setMessage] = useState("");
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // §05.4: the submit button is never disabled to express invalidity. The
  // form submits, the address is checked here (and on blur), and the fix is
  // written inline as the field's aria-describedby target with aria-invalid
  // set — not the browser's native bubble, which is why the form is
  // noValidate. `emailError` is a string so the message and the flag cannot
  // disagree.
  const [emailError, setEmailError] = useState<string | null>(null);
  const emailErrorId = `${inputId}-error`;
  const isValidEmail = (value: string) => EMAIL_REGEX.test(value.trim());

  // A pointer press inside the form (the submit button, the checkbox) blurs
  // the field BEFORE the click lands. If blur inserted the message then, the
  // button would move ~20px out from under the pointer between mousedown and
  // mouseup and the click would never fire - measured in Chromium: no submit
  // event at all. So blur validation stands down while a pointer is down on
  // the form: set on pointerdown, cleared by the next pointerup/pointercancel
  // anywhere (the release may land outside the form). Submit validates
  // anyway, and keyboard (Tab) blur still validates.
  const pointerInsideForm = useRef(false);
  const notePointerDown = useCallback(() => {
    pointerInsideForm.current = true;
    const release = () => {
      pointerInsideForm.current = false;
      window.removeEventListener("pointerup", release, true);
      window.removeEventListener("pointercancel", release, true);
    };
    window.addEventListener("pointerup", release, true);
    window.addEventListener("pointercancel", release, true);
  }, []);

  const handleBlur = useCallback(() => {
    if (pointerInsideForm.current) return;
    if (email.trim() === "") return; // an untouched field is not yet wrong
    setEmailError(isValidEmail(email) ? null : EMAIL_FORMAT_MESSAGE);
  }, [email]);

  const handleChange = useCallback(
    (value: string) => {
      setEmail(value);
      // Clear the message the moment the address becomes valid; never
      // re-raise it while typing (that is blur's and submit's job).
      if (emailError && isValidEmail(value)) setEmailError(null);
    },
    [emailError, setEmail]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!isValidEmail(email)) {
        setEmailError(EMAIL_FORMAT_MESSAGE);
        inputRef.current?.focus();
        return;
      }
      setEmailError(null);

      setState("loading");
      try {
        const headers = await withRequestProofHeaders({ "Content-Type": "application/json" });
        const res = await fetch("/api/email-sound", {
          method: "POST",
          headers,
          body: JSON.stringify({
            email: email.trim(),
            soundId,
            joinNewsletter,
            website: honeypot,
          }),
        });
        const data = (await res.json()) as { message?: string; error?: string };
        if (res.ok) {
          setState("done");
          rememberEmail(email);
          setMessage(
            data.message ??
              "Check your inbox. Open that email on the computer you'll use for the USB drive; it has the file plus the 3 install steps."
          );
          trackEmailFileRequested({
            sound_id: soundId,
            join_newsletter: joinNewsletter,
            email_surface: surface,
          });
        } else {
          setState("error");
          setMessage(
            data.error ??
              "Couldn't send right now. Try again in a moment, or download the file directly."
          );
        }
      } catch {
        setState("error");
        setMessage(
          "Couldn't send right now. Try again in a moment, or download the file directly."
        );
      }
    },
    [email, honeypot, joinNewsletter, rememberEmail, soundId, surface]
  );

  return (
    <div className={cn("w-full", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`${inputId}-form`}
        className={cn(
          "tlsr-focus flex min-h-[44px] w-full items-center justify-between gap-2 text-sm transition-opacity hover:opacity-80",
          isPrimary &&
            // Mobile: a ruled co-primary disclosure. Desktop (`sm:` up) reverts to
            // the quiet muted row so the higher-converting surface is untouched.
            "border-y border-[color:var(--tls-border-subtle)] px-0 py-3 font-medium text-[color:var(--tls-fg-strong)] sm:border-0 sm:px-0 sm:py-0 sm:font-medium sm:text-[color:var(--tls-fg-muted)]"
        )}
        style={isPrimary ? undefined : { color: "var(--tls-fg-muted)" }}
      >
        <span className="flex flex-col items-start text-left">
          <span>{label}</span>
          {isPrimary && !open && (
            // Value prop for the stranded-mobile case; visual-only (the label
            // carries the accessible name). Hidden on desktop.
            <span
              aria-hidden="true"
              className="mt-0.5 text-xs font-medium sm:hidden"
              style={{ color: "var(--tls-fg-subtle)" }}
            >
              On your phone? We&apos;ll email a download link + install steps.
            </span>
          )}
        </span>
        {open ? (
          <ChevronUp className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
        )}
      </button>

      {open && (
        <div
          id={`${inputId}-form`}
          className={cn(
            "mt-2",
            isPrimary && "border-b border-[color:var(--tls-border-subtle)] pb-4"
          )}
        >
          {state === "done" ? (
            <p
              className="text-sm font-medium"
              role="status"
              style={{ color: "var(--tls-fg-strong)" }}
            >
              {message}
            </p>
          ) : (
            <form
              onSubmit={handleSubmit}
              onPointerDownCapture={notePointerDown}
              noValidate
              className="space-y-2"
            >
              <p className="text-xs" style={{ color: "var(--tls-fg-subtle)" }}>
                {helper}
              </p>

              {/* Honeypot — humans never see or fill this. */}
              <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden">
                <label>
                  Leave this field empty
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </label>
              </div>

              <input
                ref={inputRef}
                id={inputId}
                type="email"
                value={email}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                placeholder="name@example.com"
                required
                maxLength={254}
                aria-label="Email address for the download link"
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? emailErrorId : undefined}
                disabled={state === "loading"}
                className="tlsr-focus w-full rounded-none border px-3 py-2.5 text-sm"
                style={{
                  minHeight: "var(--tls-field-h)",
                  background: "var(--tls-surface)",
                  // Ink, not a semantic red: the design system has none. The
                  // strong edge plus the message underneath carry the state.
                  borderColor: emailError ? "var(--tls-fg-strong)" : "var(--tls-border)",
                  color: "var(--tls-fg-strong)",
                }}
              />
              {emailError && (
                <p
                  id={emailErrorId}
                  className="text-xs font-semibold"
                  style={{ color: "var(--tls-fg-strong)" }}
                >
                  {emailError}
                </p>
              )}
              {isPrefilled && (
                // §11.4/§11.6 WCAG 3.3.7 Redundant Entry
                <p className="text-xs" style={{ color: "var(--tls-fg-subtle)" }}>
                  Using the address you entered earlier.{" "}
                  <button
                    type="button"
                    onClick={changeEmail}
                    className="tlsr-focus underline underline-offset-2"
                    style={{ color: "var(--tls-fg-muted)" }}
                  >
                    change it
                  </button>
                </p>
              )}
              <label
                className="flex cursor-pointer items-center gap-2 text-xs"
                style={{ color: "var(--tls-fg-muted)" }}
              >
                <input
                  type="checkbox"
                  checked={joinNewsletter}
                  onChange={(e) => setJoinNewsletter(e.target.checked)}
                  className="h-3.5 w-3.5 rounded accent-[var(--tls-brand)]"
                />
                Also subscribe to Chime of the Week (~6 sounds/month)
              </label>
              <button
                type="submit"
                // Busy is the only reason this is ever disabled (§05.4).
                disabled={state === "loading"}
                aria-busy={state === "loading" || undefined}
                className="tlsr-focus w-full rounded-[var(--tls-radius-pill)] py-2.5 text-sm font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                style={{ background: "var(--tls-fg-strong)", color: "var(--tls-canvas)" }}
              >
                {state === "loading" ? "Sending…" : "Send download link"}
              </button>
              {state === "error" && (
                // Error verdict in ink at 700: weight and position carry it,
                // no semantic red (design-system colour rule).
                <p className="text-xs font-bold" role="alert" style={{ color: "var(--tls-fg-strong)" }}>
                  {message}
                </p>
              )}
            </form>
          )}
        </div>
      )}
    </div>
  );
}
